import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, Send, CalendarDays, MapPin, Phone, CalendarClock, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import { Wordmark } from "@/components/SiteHeader";
import { Stepper } from "@/components/Stepper";
import { ServicesStep } from "@/components/steps/ServicesStep";
import { CortejoStep } from "@/components/steps/CortejoStep";
import { RoteiroStep } from "@/components/steps/RoteiroStep";
import { FormationStep } from "@/components/steps/FormationStep";
import { ReceptionStep } from "@/components/steps/ReceptionStep";
import { FestaStep } from "@/components/steps/FestaStep";
import { BandaStep } from "@/components/steps/BandaStep";
import { AstroLiveStep } from "@/components/steps/AstroLiveStep";
import { ReviewStep } from "@/components/steps/ReviewStep";
import { SuccessScreen } from "@/components/SuccessScreen";
import { ItunesSearchModal } from "@/components/ItunesSearchModal";
import { AiAssistantModal } from "@/components/AiAssistantModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { momentComplete, formatDatePt, maskPhoneBR } from "@/lib/format";

const LABELS = {
  servicos: "Serviços", cortejo: "Cortejo", musicas: "Músicas", formacao: "Formação",
  recepcao: "Recepção pré", festa: "Festa", banda: "Banda", astrolive: "AstroLive",
  revisao: "Revisão", envio: "Envio",
};

function buildStepKeys(services) {
  const s = services || { cerimonia: true, banda: false, astrolive: false };
  const keys = ["servicos"];
  // Recepção pré-cerimonial vem ANTES da escolha das músicas do cerimonial
  if (s.cerimonia) keys.push("cortejo", "recepcao", "musicas", "formacao");
  if (s.banda || s.astrolive) keys.push("festa");
  if (s.banda) keys.push("banda");
  if (s.astrolive) keys.push("astrolive");
  keys.push("revisao", "envio");
  return keys;
}

export default function Wizard() {
  const { token } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const [saveState, setSaveState] = useState("idle");
  const [confirmed, setConfirmed] = useState(false);
  const [pendingIds, setPendingIds] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [searchMomentId, setSearchMomentId] = useState(null);
  const [aiMomentId, setAiMomentId] = useState(null);
  const saveTimer = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${token}`);
      setEvent(data);
      if (data.status === "enviado" || data.locked) setSent(true);
    } catch { setNotFound(true); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Limpa o autosave pendente ao desmontar (evita PATCH tardio após sair da página)
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const persist = useCallback((payload) => {
    setSaveState("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try { await api.patch(`/events/${token}`, payload); setSaveState("saved"); }
      catch { setSaveState("idle"); }
    }, 700);
  }, [token]);

  const updateEvent = useCallback((patch) => {
    setEvent((prev) => {
      const next = { ...prev, ...patch };
      persist({
        couple_name: next.couple_name, date: next.date, local: next.local, phone: next.phone,
        services: next.services, formation: next.formation, reception: next.reception,
        moments: next.moments, favorites: next.favorites, festa: next.festa,
        banda: next.banda, astrolive: next.astrolive,
      });
      return next;
    });
  }, [persist]);

  const goStep = (s) => {
    setStep(s);
    setMaxReached((m) => Math.max(m, s));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyMusic = (momentId, selection) => {
    const moments = event.moments.map((m) => {
      if (m.id !== momentId) return m;
      if (selection.manual_only) return { ...m, manual_link: selection.link, undecided: false };
      return { ...m, music: selection, undecided: false };
    });
    updateEvent({ moments });
  };

  const addFavorite = (s) => {
    const exists = (event.favorites || []).some((f) => f.track_name === s.track_name && f.artist_name === s.artist_name);
    const favorites = exists
      ? (event.favorites || []).filter((f) => !(f.track_name === s.track_name && f.artist_name === s.artist_name))
      : [...(event.favorites || []), { track_name: s.track_name, artist_name: s.artist_name, artwork: s.artwork, preview_url: s.preview_url, link: s.link }];
    updateEvent({ favorites });
    toast.success(exists ? "Removida das favoritas" : "Adicionada às favoritas ♥");
  };

  if (loading)
    return <div className="flex min-h-screen items-center justify-center bg-[color:var(--brand-paper)]"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--brand-marsala)]" /></div>;
  if (notFound)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[color:var(--brand-paper)] px-4 text-center">
        <BrandMark size={48} />
        <h1 className="font-serif text-2xl text-[color:var(--brand-graphite)]">Evento não encontrado</h1>
        <p className="text-sm text-[color:var(--brand-muted)]">Verifique o link recebido da LC Produção.</p>
      </div>
    );

  const stepKeys = buildStepKeys(event.services);
  const stepLabels = stepKeys.map((k) => LABELS[k]);
  const key = stepKeys[step] || "servicos";
  const reviewIndex = stepKeys.indexOf("revisao");
  const successIndex = stepKeys.indexOf("envio");
  const musicIndex = stepKeys.indexOf("musicas");

  const moments = event.moments || [];
  const cerimonia = (event.services || {}).cerimonia;
  const done = moments.filter(momentComplete).length;
  const total = moments.length;
  const percent = cerimonia && total ? Math.round((done / total) * 100) : (step >= reviewIndex ? 100 : Math.round((step / reviewIndex) * 100));

  const finalize = async () => {
    if (!confirmed) { toast.error("Confirme a revisão para enviar."); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/events/${token}/finalize`);
      if (!data.ok) {
        setPendingIds(data.pending.map((p) => p.id));
        toast.error("Seu repertório ainda possui informações pendentes.");
        goStep(musicIndex >= 0 ? musicIndex : 0);
        setTimeout(() => {
          const first = document.querySelector(`[data-testid="moment-card-${data.pending[0].id}"]`);
          first && first.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 300);
        return;
      }
      setSent(true);
      goStep(successIndex);
    } catch { toast.error("Não foi possível enviar agora. Tente novamente."); }
    finally { setSending(false); }
  };

  if (sent && (key === "envio" || event.locked)) {
    return (
      <div className="min-h-screen bg-[color:var(--brand-paper)]">
        <BrandBand />
        <SuccessScreen />
      </div>
    );
  }

  const searchMoment = moments.find((m) => m.id === searchMomentId);
  const aiMoment = moments.find((m) => m.id === aiMomentId);
  const nearDeadline = event.deadline ? (new Date(event.deadline) - new Date()) / 86400000 <= 14 : false;

  return (
    <div className="min-h-screen bg-[color:var(--brand-paper)] pb-28">
      <div className="sticky top-0 z-30 border-b border-[color:var(--brand-border)] bg-white/85 backdrop-blur-md">
        <div className="h-0.5 w-full bg-gradient-to-r from-[color:var(--brand-champagne)] via-[color:var(--brand-marsala)] to-[color:var(--brand-champagne)] opacity-80" />
        <div className="mx-auto max-w-3xl px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <BrandMark size={34} />
              <div className="leading-tight">
                <p className="flex items-center gap-1.5 font-serif text-sm font-semibold text-[color:var(--brand-graphite)]">
                  {event.couple_name}
                  <button onClick={() => setEditOpen(true)} data-testid="edit-couple-button" className="text-[color:var(--brand-muted)] hover:text-[color:var(--brand-marsala)]" title="Editar dados"><Pencil className="h-3 w-3" /></button>
                </p>
                <p className="text-[11px] text-[color:var(--brand-muted)]">{formatDatePt(event.date)} · {event.local}</p>
              </div>
            </div>
            <AutosaveIndicator state={saveState} />
          </div>
          <div className="mt-3"><Stepper active={step} maxReached={maxReached} onStepClick={goStep} steps={stepLabels} /></div>
        </div>
      </div>

      {event.deadline && (
        <div className={`mx-auto mt-3 flex max-w-3xl items-center gap-2 rounded-xl border px-4 py-2.5 text-sm ${nearDeadline ? "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)] text-[color:var(--brand-marsala)]" : "border-[color:var(--brand-border)] bg-white text-[color:var(--brand-muted)]"}`}>
          <CalendarClock className="h-4 w-4" /> Seu formulário deverá ser enviado até {formatDatePt(event.deadline)}.
        </div>
      )}

      <div className="mx-auto mt-4 max-w-3xl px-4">
        <div className="flex items-center justify-between text-xs text-[color:var(--brand-muted)]">
          <span data-testid="wizard-progress-text">Etapa {step + 1} de {stepKeys.length} · {percent}% concluído</span>
          {cerimonia && <span>{done} de {total} momentos definidos</span>}
        </div>
        <Progress data-testid="wizard-progress-bar" value={percent} className="mt-1.5 h-2" />
      </div>

      <div className="mx-auto max-w-3xl px-4 py-6">
        <AnimatePresence mode="wait">
          {key === "servicos" && <ServicesStep key="svc" services={event.services} adminConfigured={event.admin_configured} onChange={(services) => updateEvent({ services })} />}
          {key === "cortejo" && <CortejoStep key="cortejo" moments={moments} onMomentsChange={(m) => updateEvent({ moments: m })} />}
          {key === "musicas" && <RoteiroStep key="mus" moments={moments} onMomentsChange={(m) => updateEvent({ moments: m })} onSearch={(id) => setSearchMomentId(id)} onAssistant={(id) => setAiMomentId(id)} pendingIds={pendingIds} />}
          {key === "formacao" && <FormationStep key="form" formation={event.formation} onChange={(formation) => updateEvent({ formation })} />}
          {key === "recepcao" && <ReceptionStep key="rec" reception={event.reception} onChange={(reception) => updateEvent({ reception })} />}
          {key === "festa" && <FestaStep key="festa" festa={event.festa} services={event.services} onChange={(festa) => updateEvent({ festa })} />}
          {key === "banda" && <BandaStep key="banda" banda={event.banda} onChange={(banda) => updateEvent({ banda })} />}
          {key === "astrolive" && <AstroLiveStep key="astro" astro={event.astrolive} products={event.astrolive_products} onChange={(astrolive) => updateEvent({ astrolive })} />}
          {key === "revisao" && <ReviewStep key="rev" event={event} confirmed={confirmed} onConfirmChange={setConfirmed} />}
        </AnimatePresence>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-[color:var(--brand-border)] bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3">
          <Button variant="outline" data-testid="wizard-back-button" disabled={step === 0} onClick={() => goStep(Math.max(0, step - 1))} className="h-11"><ArrowLeft className="mr-1.5 h-4 w-4" /> Voltar</Button>
          {step < reviewIndex ? (
            <Button data-testid="wizard-next-button" onClick={() => goStep(step + 1)} className="h-11 flex-1 bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)] sm:flex-none sm:px-8">Continuar <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
          ) : (
            <Button data-testid="finalize-button" onClick={finalize} disabled={sending} className="h-11 flex-1 bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)] sm:flex-none sm:px-8">
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Enviar para a LC Produção
            </Button>
          )}
        </div>
      </div>

      {searchMoment && <ItunesSearchModal open={!!searchMomentId} onClose={() => setSearchMomentId(null)} momentName={searchMoment.name} onSelect={(sel) => applyMusic(searchMomentId, sel)} />}
      {aiMoment && (
        <AiAssistantModal open={!!aiMomentId} onClose={() => setAiMomentId(null)} momentName={aiMoment.name} favorites={event.favorites || []}
          onSelect={(s) => { applyMusic(aiMomentId, { track_id: s.track_id, track_name: s.track_name, artist_name: s.artist_name, artwork: s.artwork, duration: s.duration, preview_url: s.preview_url, link: s.link }); setAiMomentId(null); toast.success("Música escolhida!"); }}
          onFavorite={addFavorite} />
      )}

      <EditCoupleDialog open={editOpen} onClose={() => setEditOpen(false)} event={event} onSave={(patch) => { updateEvent(patch); setEditOpen(false); toast.success("Dados atualizados."); }} />
    </div>
  );
}

const BrandBand = () => (
  <div className="bg-[color:var(--brand-graphite)]">
    <div className="h-[3px] w-full bg-gradient-to-r from-transparent via-[color:var(--brand-champagne)] to-transparent opacity-80" />
    <div className="flex justify-center py-4">
      <Wordmark dark size={38} tagline={null} />
    </div>
  </div>
);

const AutosaveIndicator = ({ state }) => (
  <div data-testid="autosave-indicator" className="text-xs text-[color:var(--brand-muted)]">
    {state === "saving" && <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Salvando...</span>}
    {state === "saved" && <span className="inline-flex items-center gap-1 text-[color:var(--brand-success)]"><Check className="h-3.5 w-3.5" /> Alterações salvas</span>}
  </div>
);

const EditCoupleDialog = ({ open, onClose, event, onSave }) => {
  const [f, setF] = useState({ couple_name: "", date: "", local: "", phone: "" });
  useEffect(() => { if (event) setF({ couple_name: event.couple_name, date: event.date, local: event.local, phone: event.phone }); }, [event, open]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-serif text-xl">Dados do evento</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome do casal</Label><Input value={f.couple_name} onChange={(e) => setF({ ...f, couple_name: e.target.value })} className="mt-1 h-11" /></div>
          <div><Label>Data do evento</Label><div className="relative mt-1"><CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--brand-muted)]" /><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="h-11 pl-9" /></div></div>
          <div><Label>Local</Label><div className="relative mt-1"><MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--brand-muted)]" /><Input value={f.local} onChange={(e) => setF({ ...f, local: e.target.value })} className="h-11 pl-9" /></div></div>
          <div><Label>WhatsApp</Label><div className="relative mt-1"><Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--brand-muted)]" /><Input value={f.phone} onChange={(e) => setF({ ...f, phone: maskPhoneBR(e.target.value) })} className="h-11 pl-9" /></div></div>
        </div>
        <DialogFooter><Button onClick={() => onSave(f)} className="bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)]">Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
